import { useCallback, useEffect, useRef } from "react";

import { prefetchNavPath } from "@/app/routeModules";

/** Dwell before a hover/focus counts as intent — avoids fetching on a flick. */
const DEFAULT_DWELL_MS = 200;

/**
 * Hover-intent route prefetch (v1's delayed-fetch pattern applied to nav).
 * Returns handlers to spread onto a nav target: after the pointer/focus rests
 * for `dwellMs`, the route's chunk(s) are warmed via `prefetchNavPath`. Leaving
 * before the dwell elapses cancels, so quick pointer sweeps cost nothing.
 *
 * On touch there is no hover, so `onPointerDown` warms immediately (the tap is
 * already committed intent).
 */
export function useHoverPrefetch(dwellMs: number = DEFAULT_DWELL_MS) {
  const timerRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(
    (path: string) => {
      cancel();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        prefetchNavPath(path);
      }, dwellMs);
    },
    [cancel, dwellMs],
  );

  const immediate = useCallback((path: string) => {
    prefetchNavPath(path);
  }, []);

  useEffect(() => cancel, [cancel]);

  return { start, cancel, immediate };
}

/** Dwell before a row hover counts as drill-in intent. */
const DEFAULT_ROW_DWELL_MS = 350;

/**
 * Generic hover-intent runner: after the pointer rests for `dwellMs`, invoke
 * `action`. Used by list pages to prefetch a row's detail chunk + payload only
 * when the user lingers, cancelling on quick sweeps. Dedupe/caching is left to
 * the action (route prefetch and `requestCache` are both idempotent).
 */
export function useDwellAction(dwellMs: number = DEFAULT_ROW_DWELL_MS) {
  const timerRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(
    (action: () => void) => {
      cancel();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        action();
      }, dwellMs);
    },
    [cancel, dwellMs],
  );

  useEffect(() => cancel, [cancel]);

  return { start, cancel };
}
